import { useMemo, type ComponentType, type ReactNode, type SVGProps } from "react";
import { toast } from "sonner";

import { Icon } from "@/components/Icon";
import {
  useActiveConnections,
  useConfigApply,
  useDashboardSummary,
} from "@/hooks/useDashboard";
import { usePositionsStream } from "@/hooks/usePositionsStream";
import { extractApiError } from "@/lib/api";
import { fixQualityMeta } from "@/lib/fixQuality";
import { GNSS_DEVICES } from "@/lib/gnssDevices";
import { formatBytes, formatDateTime } from "@/lib/utils";

/** Etiqueta corta amigable para operador (FIX / FLOAT / DIF) sobre los nombres
 *  estándar NMEA. Para estados raros caemos al label de fixQualityMeta. */
function shortQualityLabel(q: number): string {
  switch (q) {
    case 4: return "FIX";
    case 5: return "FLOAT";
    case 2: return "DIF";
    case 1: return "SINGLE";
    default: return fixQualityMeta(q).label.toUpperCase();
  }
}

export function DashboardPage() {
  const summary = useDashboardSummary();
  const connections = useActiveConnections();
  const apply = useConfigApply();
  // Stream WS de posiciones — solo lo usamos aquí para mostrar la calidad
  // del fix (FIX / FLOAT / DIF / ...) junto al chip Rol del rover.
  const stream = usePositionsStream({ enabled: true });

  const live =
    !summary.isLoading && !connections.isLoading && summary.isFetching === false;

  const sortedConnections = useMemo(() => {
    if (!connections.data) return [];
    return [...connections.data].sort((a, b) =>
      a.connected_since < b.connected_since ? 1 : -1,
    );
  }, [connections.data]);

  const handleApply = async () => {
    try {
      const res = await apply.mutateAsync();
      toast.success(
        `Configuración aplicada — ${res.regenerated_files.length} archivo(s) regenerado(s)`,
      );
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText("caster.amelia.am:2101");
    toast.success("URL del caster copiada");
  };

  const mountpointsTotal = summary.data?.mountpoints_total ?? 0;
  const mountpointsEnabled = summary.data?.mountpoints_enabled ?? 0;
  const clientsTotal = summary.data?.clients_total ?? 0;
  const sessionsActive = summary.data?.sessions_active ?? 0;

  return (
    <div className="view">
      <div className="view__head">
        <div className="view__title-block">
          <div className="view__eyebrow">
            <span className="tick" /> Visión general
          </div>
          <h1 className="view__title">
            Dashboard
            <LivePill active={live} />
          </h1>
          <p className="view__subtitle">
            Estado en vivo del caster. Las sesiones se refrescan cada 2s; los totales cada 5s.
          </p>
        </div>
        <div className="view__actions">
          <button className="btn" onClick={handleCopyUrl}>
            <Icon.Copy />
            Copiar URL del caster
          </button>
          <button
            className="btn btn--primary"
            onClick={handleApply}
            disabled={apply.isPending}
          >
            {apply.isPending ? <span className="spinner" /> : <Icon.Spark />}
            Forzar configuración
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats">
        <Stat
          label="Mountpoints"
          value={mountpointsEnabled}
          suffix={`/ ${mountpointsTotal}`}
          caption={
            <>
              En línea · <b>NTRIP 2.0</b>
            </>
          }
          icon={Icon.Radio}
          loading={summary.isLoading}
        />
        <Stat
          label="Clientes"
          value={clientsTotal}
          caption={
            <>
              <b>{summary.data?.clients_enabled ?? 0}</b> habilitados
            </>
          }
          icon={Icon.Clients}
          loading={summary.isLoading}
        />
        <Stat
          label="Sesiones activas"
          value={sessionsActive}
          caption={<>Publishers + consumers</>}
          icon={Icon.Activity}
          loading={summary.isLoading}
        />
        <Stat
          label="Administradores"
          value={null}
          caption={
            <>
              Ver pestaña <b>Administradores</b>
            </>
          }
          icon={Icon.Users}
          loading={false}
        />
      </div>

      {/* Sesiones en vivo */}
      <div className="card card--ticks">
        <div className="card__head">
          <h2 className="card__title">
            <span className="reticle" />
            Sesiones en vivo
          </h2>
          <div className="row">
            <span className={`badge ${live ? "badge--ok" : "badge--neutral"}`}>
              {live ? "Streaming" : "—"}
            </span>
          </div>
        </div>
        <div>
          {connections.isLoading ? (
            <div style={{ padding: 20 }}>
              <span className="sk" style={{ height: 80 }} />
            </div>
          ) : sortedConnections.length === 0 ? (
            <div className="empty">
              <div className="empty__reticle">
                <Icon.Clients style={{ width: 18, height: 18 }} />
              </div>
              <div className="empty__title">Sin sesiones activas</div>
              <div className="empty__sub">
                Cuando un cliente NTRIP se conecte aparecerá aquí en tiempo real.
              </div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Rol</th>
                  <th>Mountpoint</th>
                  <th>Conectado desde</th>
                  <th style={{ textAlign: "right", paddingRight: 24 }}>Bytes recibidos</th>
                </tr>
              </thead>
              <tbody>
                {sortedConnections.map((s) => {
                  const rover = s.role === "consumer" ? stream.rovers.get(s.session_id) : null;
                  const qMeta = rover ? fixQualityMeta(rover.quality) : null;
                  return (
                  <tr key={`${s.client_username}-${s.connected_since}`}>
                    <td className="tbl__mono">{s.client_username}</td>
                    <td>
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <span
                          className={`badge ${s.role === "publisher" ? "badge--ok" : "badge--info"}`}
                        >
                          {s.role === "publisher" ? "Publisher" : "Rover"}
                        </span>
                        {rover?.stale && (
                          <span className="badge badge--off" title="Sin GGA recientes">
                            sin datos
                          </span>
                        )}
                        {qMeta && rover && !rover.stale && (
                          <span
                            className="badge"
                            title={`GGA quality ${rover.quality} · ${qMeta.label}`}
                            style={{
                              background: `${qMeta.hex}1f`,
                              color: qMeta.hex,
                              borderColor: `${qMeta.hex}55`,
                            }}
                          >
                            {shortQualityLabel(rover.quality)}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span className="badge">{s.mountpoint}</span>
                    </td>
                    <td className="muted mono" style={{ fontSize: 12 }}>
                      {formatDateTime(s.connected_since)}
                    </td>
                    <td
                      className="tbl__mono"
                      style={{ textAlign: "right", paddingRight: 24 }}
                    >
                      {formatBytes(s.bytes_received)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Galería de receptores GNSS compatibles */}
      <div className="section-gap">
        <div className="card card--ticks">
          <div className="card__head">
            <div>
              <h2 className="card__title">
                <span className="reticle" />
                Receptores GNSS compatibles
              </h2>
              <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                Cualquier cliente NTRIP — RTCM 3.x · CMR+ · GPS+GLO+GAL+BDS
              </div>
            </div>
          </div>
          <div className="card__body">
            <div className="gps-grid">
              {GNSS_DEVICES.map((d) => (
                <div key={d.id} className="gps-card">
                  <div className="gps-card__shot">
                    <img src={d.img} alt={`${d.brand} ${d.model}`} />
                  </div>
                  <div className="gps-card__brand">{d.brand}</div>
                  <div className="gps-card__name">{d.model}</div>
                  <div className="gps-card__meta">
                    <span className="dot" />
                    <span style={{ marginLeft: "auto", opacity: 0.6 }}>{d.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatProps {
  label: string;
  value: number | null;
  suffix?: string;
  caption: ReactNode;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  loading: boolean;
}

function Stat({ label, value, suffix, caption, icon: I, loading }: StatProps) {
  return (
    <div className="stat card--ticks">
      <div className="stat__ruler" />
      <div className="stat__label">
        <span>{label}</span>
        <span className="stat__icon">
          <I />
        </span>
      </div>
      <div className="stat__value">
        {loading ? (
          <span className="sk" style={{ height: 28, width: 56 }} />
        ) : value == null ? (
          "—"
        ) : (
          value
        )}
        {suffix && <span className="stat__suffix">{suffix}</span>}
      </div>
      <div className="stat__caption">{caption}</div>
    </div>
  );
}

function LivePill({ active }: { active: boolean }) {
  return (
    <span className={`live-pill ${active ? "" : "live-pill--idle"}`}>
      {active ? "live" : "idle"}
    </span>
  );
}
